import { createFileRoute } from "@tanstack/react-router";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { siteConfig } from "@/config";

export const Route = createFileRoute("/editorial-policy")({
  head: () => {
    const title = `Editorial Policy — ${siteConfig.siteName}`;
    const desc = `How ${siteConfig.siteName} researches, tests, and publishes CRM reviews and buyer's guides — our sourcing, fact-checking, and correction policy.`;
    const url = `${siteConfig.domain}/editorial-policy`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: EditorialPolicy,
});

function EditorialPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-3xl px-4 py-12">
        <h1 className="text-4xl font-bold tracking-tight">Editorial Policy</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          Last updated: January 2026
        </p>
        <div className="prose-article mt-8 space-y-6 text-base leading-relaxed">
          <p>
            {siteConfig.siteName} exists to help revenue teams choose CRM
            software that actually fits their pipeline. Every article on this
            site is written and edited by humans on our editorial team, then
            reviewed against the process below before it is published.
          </p>

          <h2 className="text-2xl font-semibold">Sourcing</h2>
          <p>
            We combine three sources for every review: (1) a hands-on pilot in
            which we build a live pipeline, connect at least one email or
            calendar integration, and run test workflows for a minimum of two
            weeks; (2) primary interviews with operators using the product in
            production — sales managers, RevOps leads, and founders — recorded
            with consent; and (3) public documentation, changelogs, and pricing
            pages verified on the vendor's own site at the time of writing.
          </p>

          <h2 className="text-2xl font-semibold">Fact-checking</h2>
          <p>
            Before publication, every claim about pricing, feature availability,
            integration support, and quotas is checked against the vendor's own
            documentation. Screenshots and pricing pages are dated. When a
            vendor ships a change that materially affects an existing article,
            we update the article and note the change at the top.
          </p>

          <h2 className="text-2xl font-semibold">Independence</h2>
          <p>
            Vendors do not preview, edit, or approve our reviews. We do not
            accept payment in exchange for coverage, ranking position, or
            favorable language. Some links in our articles are affiliate links —
            see our{" "}
            <a href="/affiliate-disclosure" className="underline">
              affiliate disclosure
            </a>{" "}
            for details. Affiliate relationships never influence rankings or
            recommendations; a product either earns its spot in a pilot or it
            does not.
          </p>

          <h2 className="text-2xl font-semibold">AI use</h2>
          <p>
            We use AI tools to help draft outlines, summarize interview
            transcripts, and check grammar. Every article is written, edited,
            and fact-checked by a human editor before it goes live. We do not
            publish AI-generated content that has not been reviewed and revised
            by a person on our team.
          </p>

          <h2 className="text-2xl font-semibold">Corrections</h2>
          <p>
            If you spot an error, email{" "}
            <a
              href={`mailto:${siteConfig.contactEmail}`}
              className="underline"
            >
              {siteConfig.contactEmail}
            </a>{" "}
            with the URL and the specific claim you believe is wrong. We aim to
            respond within three business days and, where a correction is
            warranted, update the article with a dated note.
          </p>

          <h2 className="text-2xl font-semibold">Contact</h2>
          <p>
            Editorial inquiries, pitches, and product briefings:{" "}
            <a
              href={`mailto:${siteConfig.contactEmail}`}
              className="underline"
            >
              {siteConfig.contactEmail}
            </a>
            .
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
}
